import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI, IMAGE_BASE_URL } from '../../api';
import { MagnifyingGlass as Search, CaretLeft as ChevronLeft, ArrowRight, Buildings as Building2, Shield } from '@phosphor-icons/react';

const FindBusiness = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const searchTenants = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      setHasSearched(false);
      return;
    }
    setLoading(true);
    try {
      const response = await authAPI.searchTenants(query);
      setResults(response.data.tenants || []);
      setHasSearched(true);
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => { searchTenants(searchQuery); }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchTenants]);

  const handleBusinessClick = (slug) => { navigate(`/${slug}/login`); };

  return (
    <div className="min-h-screen bg-primaryB-50">
      {/* Header */}
      <div className="bg-primaryB-50/90 backdrop-blur-xl border-b border-primaryB-200/60">
        <div className="max-w-2xl mx-auto px-5 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-primaryB-500 hover:text-primaryB-900 transition-all duration-200">
              <ChevronLeft weight="bold" className="w-5 h-5" />
              <span className="text-sm font-semibold">Back</span>
            </Link>
            <Link to="/" className="flex items-center gap-2">
              <img src="/images/logo.png" alt="SmartPOS Africa" className="w-8 h-8 rounded-xl" />
              <span className="text-primaryB-900 font-extrabold">Smart POS</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-5 py-12">
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-primaryB-900 mb-2 tracking-tight">
            Find your business
          </h1>
          <p className="text-primaryB-500 text-sm sm:text-base">
            Search for your business to sign in to your account
          </p>
        </div>

        {/* Search Input */}
        <div className="relative mb-6">
          <Search weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-primaryB-300" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Enter your business name..."
            className="w-full pl-12 pr-4 py-4 bg-primaryB-50 border border-primaryB-200/60 rounded-xl text-primaryB-900 placeholder-primaryB-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base sm:text-lg"
            autoFocus
          />
          {loading && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>

        {/* No results */}
        {hasSearched && results.length === 0 && !loading && (
          <div className="bg-primaryB-50 rounded-3xl border border-primaryB-200/60 p-8 text-center">
            <p className="text-primaryB-900 font-bold mb-1">No businesses found</p>
            <p className="text-primaryB-300 text-sm mb-6">
              We couldn't find a business matching "{searchQuery}"
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/signup"
                className="group inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-full font-bold transition-all duration-200"
              >
                Register Your Business
                <ArrowRight weight="bold" className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>
        )}

        {/* Results */}
        {results.length > 0 && (
          <div className="bg-primaryB-50 rounded-3xl border border-primaryB-200/60 overflow-hidden">
            <div className="px-4 py-3 border-b border-primaryB-200/60">
              <p className="text-sm text-primaryB-300">
                {results.length} business{results.length !== 1 ? 'es' : ''} found
              </p>
            </div>
            <div className="divide-y divide-primaryB-200/60">
              {results.map((tenant) => (
                <button
                  key={tenant.id}
                  onClick={() => handleBusinessClick(tenant.slug)}
                  className="w-full flex items-center gap-4 p-4 hover:bg-primaryB-50 transition-all duration-200 text-left"
                >
                  {tenant.businessLogo ? (
                    <img
                      src={`${IMAGE_BASE_URL}${tenant.businessLogo}`}
                      alt={tenant.businessName}
                      className="w-12 h-12 rounded-full object-cover bg-primaryB-50"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-primaryB-50 rounded-full flex items-center justify-center">
                      <Building2 weight="bold" className="w-6 h-6 text-primaryB-300" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-primaryB-900 font-bold truncate">{tenant.businessName}</p>
                    <p className="text-primaryB-300 text-sm">{tenant.businessType?.replace(/_/g, ' ') || 'Business'}</p>
                  </div>
                  <ArrowRight weight="bold" className="w-5 h-5 text-primaryB-300" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Help Links */}
        <div className="mt-8 space-y-3">
          <Link
            to="/signup"
            className="flex items-center gap-4 p-4 bg-primaryB-50 hover:bg-primaryB-50 rounded-2xl border border-primaryB-200/60 transition-all duration-200"
          >
            <Building2 weight="bold" className="w-5 h-5 text-primaryB-300" />
            <div className="flex-1">
              <p className="text-primaryB-900 font-semibold text-sm">Don't have an account?</p>
              <p className="text-primaryB-300 text-xs">Start your free 1 month trial</p>
            </div>
            <ArrowRight weight="bold" className="w-4 h-4 text-primaryB-300" />
          </Link>

          <Link
            to="/admin/login"
            className="flex items-center gap-4 p-4 bg-primaryB-50 hover:bg-primaryB-50 rounded-2xl border border-primaryB-200/60 transition-all duration-200"
          >
            <Shield weight="bold" className="w-5 h-5 text-primaryB-300" />
            <div className="flex-1">
              <p className="text-primaryB-900 font-semibold text-sm">Platform Administrator?</p>
              <p className="text-primaryB-300 text-xs">Access super admin panel</p>
            </div>
            <ArrowRight weight="bold" className="w-4 h-4 text-primaryB-300" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FindBusiness;
